import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { usePurchaseOrderStore, useSupplierStore, useProductStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Input, Select } from '@lancar-pos/ui';
import type { PurchaseOrderItem } from '@lancar-pos/db-schema';

export function PurchaseOrderFormPage() {
  const navigate = useNavigate();
  
  const { addPurchaseOrder } = usePurchaseOrderStore();
  const { suppliers } = useSupplierStore();
  const { products } = useProductStore();
  const toast = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    supplierId: '',
    expectedDate: '',
    notes: '',
  });
  const [items, setItems] = useState<PurchaseOrderItem[]>([]);

  const supplierOptions = useMemo(() => {
    return suppliers
      .filter((s) => !s._deleted && s.isActive)
      .map((s) => ({ value: s.id, label: s.name }));
  }, [suppliers]);

  const productOptions = useMemo(() => {
    return products
      .filter((p) => !p._deleted && p.isActive)
      .map((p) => ({ value: p.id, label: p.name }));
  }, [products]);

  const totals = useMemo(() => {
    const totalItems = items.length;
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);
    return { totalItems, totalQuantity, totalAmount };
  }, [items]);

  const addItem = () => {
    const newItem: PurchaseOrderItem = {
      id: crypto.randomUUID(),
      productId: '',
      productName: '',
      quantity: 1,
      receivedQuantity: 0,
      unitPrice: 0,
      totalPrice: 0,
      notes: '',
    };
    setItems([...items, newItem]);
  };

  const updateItem = (index: number, updates: Partial<PurchaseOrderItem>) => {
    const newItems = [...items];
    const item = { ...newItems[index], ...updates };
    
    if (updates.productId) {
      const product = products.find((p) => p.id === updates.productId);
      if (product) {
        item.productName = product.name;
      }
    }
    
    item.totalPrice = item.quantity * item.unitPrice;
    newItems[index] = item;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supplierId) {
      toast.error('Supplier wajib dipilih');
      return;
    }

    if (items.length === 0) {
      toast.error('Minimal harus ada 1 produk');
      return;
    }

    const validItems = items.filter((item) => item.productId && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Minimal harus ada 1 produk yang valid');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await addPurchaseOrder({
        supplierId: formData.supplierId,
        status: 'draft',
        expectedDate: formData.expectedDate || new Date().toISOString(),
        receivedDate: '',
        items: validItems,
        totalItems: totals.totalItems,
        totalQuantity: totals.totalQuantity,
        totalAmount: totals.totalAmount,
        notes: formData.notes,
        createdBy: '',
      });
      toast.success('Pesanan pembelian berhasil dibuat');
      navigate({ to: '/app/inventory/purchase-orders' });
    } catch (error) {
      toast.error('Gagal membuat pesanan pembelian');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app/inventory/purchase-orders' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Pesanan Pembelian Baru
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-6">
        <form onSubmit={handleSubmit}>
          <Card className="mb-4">
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Supplier"
                  placeholder="Pilih supplier"
                  value={formData.supplierId}
                  onChange={(value: string) => setFormData({ ...formData, supplierId: value })}
                  options={supplierOptions}
                />
                
                <Input
                  label="Tanggal Diharapkan"
                  value={formData.expectedDate}
                  onChange={(value: string) => setFormData({ ...formData, expectedDate: value })}
                  placeholder="YYYY-MM-DD"
                />
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Catatan</label>
                <textarea
                  value={formData.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Catatan pesanan"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Produk</h2>
                <Button type="button" variant="secondary" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-2" />Tambah Produk
                </Button>
              </div>
              
              {items.length === 0 ? (
                <p className="text-center text-gray-500 py-4">Belum ada produk ditambahkan</p>
              ) : (
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={item.id} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <Select
                          placeholder="Pilih produk"
                          value={item.productId}
                          onChange={(value: string) => updateItem(index, { productId: value })}
                          options={productOptions}
                        />
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity.toString()}
                          onChange={(value: string) => updateItem(index, { quantity: parseInt(value) || 0 })}
                        />
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          placeholder="Harga"
                          value={item.unitPrice.toString()}
                          onChange={(value: string) => updateItem(index, { unitPrice: parseInt(value) || 0 })}
                        />
                      </div>
                      <div className="w-32 text-right py-2">
                        Rp {(item.quantity * item.unitPrice).toLocaleString('id-ID')}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 hover:bg-gray-200 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span>Total Items: {totals.totalItems}</span>
                  <span>Total Qty: {totals.totalQuantity}</span>
                  <span className="font-semibold">Total: Rp {totals.totalAmount.toLocaleString('id-ID')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => navigate({ to: '/app/inventory/purchase-orders' })}
            >
              Batal
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Menyimpan...' : 'Simpan sebagai Draft'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
